import { AnalysisResult, SymptomAnalysisInput, Recommendation, TriageResult, Source, AgeLifeGuidelines, InteractionAlert } from '@/types/naturopathy';
import { supabase } from '@/integrations/supabase/client';
import { 
  findResourcesByCategoryAndSymptoms, 
  TreatmentResource,
  aromatherapyData,
  gemmotherapyData,
  clayData,
  vegetableOilsData
} from '@/data/treatmentLibraryData';
import { 
  getAgeCategory, 
  ageCategories, 
  plantsToAvoid, 
  recommendedForms,
  AgeCategory 
} from '@/data/ageGuidelines';
import {
  identifyMedicationType,
  plantMedicationInteractions,
  getAllergyWarningsForPatient,
  isPlantContraindicatedForAllergies,
  checkPlantMedicationInteraction,
  generateMedicationAllergySummary,
  MedicationType,
  PlantMedicationInteraction,
  AllergyWarning
} from '@/data/medicationInteractions';

// Service de simulation pour l'analyse naturopathique
class NaturopathyService {
  private redFlags = [
    'douleur thoracique',
    'dyspnée',
    'fièvre élevée',
    'fièvre 39',
    'fièvre 40',
    'saignement',
    'perte de conscience',
    'convulsion',
    'paralysie',
    'trouble neurologique',
    'douleur abdominale intense',
    'vomissement de sang'
  ];

  async analyzeSymptoms(input: SymptomAnalysisInput, userId?: string): Promise<AnalysisResult> {
    // Simulation d'un délai d'analyse
    await new Promise(resolve => setTimeout(resolve, 2000));

    const symptomsLower = input.symptoms.toLowerCase();
    
    // Vérification des red flags
    const triage = this.performTriage(symptomsLower);
    
    // Si red flags, pas de recommandations
    if (triage.level === 'urgent_medical') {
      return {
        triage,
        recommendations: [],
        sources: [],
        interactions_alert: [],
        explanations: "En raison des symptômes décrits, une consultation médicale urgente est recommandée avant toute approche naturopathique.",
        confidence_score: 0.95,
        timestamp: new Date().toISOString()
      };
    }

    // Génération des recommandations basées sur les symptômes
    const recommendations = await this.generateRecommendations(symptomsLower, input, userId);
    
    // Vérification des interactions médicamenteuses et allergies
    const interactions_alert = this.checkInteractions(input.medications || [], input.allergies || [], recommendations);
    
    // Génération des sources
    const sources = this.generateSources(symptomsLower);
    
    // Génération des explications
    const explanations = this.generateExplanations(symptomsLower, input);
    
    // Génération des informations liées à l'âge/état physiologique
    const ageLifeGuidelines = this.generateAgeLifeGuidelines(input);

    return {
      triage,
      recommendations,
      sources,
      interactions_alert,
      explanations,
      confidence_score: this.calculateConfidenceScore(symptomsLower, input),
      timestamp: new Date().toISOString(),
      ageLifeGuidelines
    };
  }

  // Générer les informations complètes sur les précautions liées à l'âge/état physiologique
  private generateAgeLifeGuidelines(input: SymptomAnalysisInput): AgeLifeGuidelines | undefined {
    const ageCategory = getAgeCategory(input.age || 30, input.isPregnant, input.isBreastfeeding);
    
    if (!ageCategory) return undefined;
    
    const categoryPlantsToAvoid = plantsToAvoid[ageCategory.id] || [];
    
    return {
      category: ageCategory.id,
      label: ageCategory.label,
      ageRange: ageCategory.ageRange,
      description: ageCategory.description,
      allowedForms: ageCategory.allowedForms,
      forbiddenForms: ageCategory.forbiddenForms,
      safePlants: ageCategory.safePlants,
      plantsToAvoid: categoryPlantsToAvoid,
      warnings: ageCategory.warnings,
      dosageNotes: ageCategory.dosageNotes
    };
  }

  private performTriage(symptoms: string): TriageResult {
    const detectedRedFlags = this.redFlags.filter(flag => symptoms.includes(flag));
    
    if (detectedRedFlags.length > 0) {
      return {
        level: 'urgent_medical',
        message: "Les symptômes décrits nécessitent une évaluation médicale immédiate. Consultez un médecin ou appelez le 15 en cas d'urgence.",
        redFlags: detectedRedFlags
      };
    }

    // Vérification de symptômes nécessitant une attention médicale
    const attentionSymptoms = ['fièvre persistante', 'douleur intense', 'perte de poids importante'];
    const needsAttention = attentionSymptoms.some(symptom => symptoms.includes(symptom));
    
    if (needsAttention) {
      return {
        level: 'attention',
        message: "Il est recommandé de consulter un professionnel de santé pour ces symptômes, en complément des approches naturopathiques.",
        redFlags: []
      };
    }

    return {
      level: 'none',
      redFlags: []
    };
  }

  private async fetchPractitionerTreatments(symptoms: string, userId?: string): Promise<Recommendation[]> {
    if (!userId) return [];

    try {
      const { data: treatments, error } = await supabase
        .from('practitioner_treatments')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching practitioner treatments:', error);
        return [];
      }

      if (!treatments || treatments.length === 0) return [];

      const symptomsLower = symptoms.toLowerCase();
      
      // Filter treatments that match symptom keywords
      const matchingTreatments = treatments.filter(treatment => 
        treatment.symptom_keywords?.some((keyword: string) => 
          symptomsLower.includes(keyword.toLowerCase())
        )
      );

      // Convert to Recommendation format
      return matchingTreatments.map(treatment => ({
        type: treatment.treatment_type as Recommendation['type'],
        title: treatment.title,
        detail: treatment.detail,
        posology: treatment.posology || undefined,
        contraindications: treatment.contraindications || [],
        priority: treatment.priority as 1 | 2 | 3,
        confidence: 0.85
      }));
    } catch (error) {
      console.error('Error in fetchPractitionerTreatments:', error);
      return [];
    }
  }

  private async fetchMedicinalPlants(symptoms: string, input: SymptomAnalysisInput): Promise<Recommendation[]> {
    try {
      const { data: plants, error } = await supabase
        .from('medicinal_plants')
        .select('*');

      if (error) {
        console.error('Error fetching medicinal plants:', error);
        return [];
      }

      if (!plants || plants.length === 0) return [];

      const symptomsLower = symptoms.toLowerCase();
      const patientAge = input.age;
      const ageCategory = getAgeCategory(patientAge || 30, input.isPregnant, input.isBreastfeeding);
      
      // Filter plants that match symptom keywords or effects
      const matchingPlants = plants.filter(plant => 
        plant.keywords?.some((keyword: string) => 
          symptomsLower.includes(keyword.toLowerCase())
        ) ||
        plant.main_effects?.some((effect: string) => 
          symptomsLower.includes(effect.toLowerCase())
        )
      );

      // Convert to Recommendation format (max 3 plants to avoid overwhelming)
      return matchingPlants.slice(0, 3).map(plant => {
        const ageRestriction = this.extractAgeRestriction(plant.contraindications || []);
        const plantName = plant.common_names[0] || plant.latin_name;
        
        // Vérifier si la plante est à éviter pour cette catégorie d'âge/état
        const isPlantToAvoid = ageCategory ? 
          (plantsToAvoid[ageCategory.id] || []).some(p => 
            plantName.toLowerCase().includes(p.toLowerCase()) || 
            plant.latin_name.toLowerCase().includes(p.toLowerCase())
          ) : false;
        
        const isChild = patientAge !== undefined && patientAge <= 16;
        const isInfant = patientAge !== undefined && patientAge < 3;
        const isElderly = patientAge !== undefined && patientAge >= 65;
        const isPregnant = input.isPregnant === true;
        const isBreastfeeding = input.isBreastfeeding === true;
        
        // Déterminer si non adapté selon l'âge/état
        let notSuitableForChildren = false;
        let childrenWarning = '';
        let minimumAge: number | undefined;
        
        // Vérification grossesse
        if (isPregnant) {
          const safePlantsPregnant = ageCategories.find(c => c.id === 'pregnant')?.safePlants || [];
          const isSafeForPregnant = safePlantsPregnant.some(sp => plantName.toLowerCase().includes(sp.toLowerCase()));
          if (!isSafeForPregnant || isPlantToAvoid) {
            notSuitableForChildren = true;
            childrenWarning = '⚠️ NON RECOMMANDÉ PENDANT LA GROSSESSE. Risques possibles : avortement, prématurité, malformations. Principe de précaution.';
          }
        }
        // Vérification allaitement
        else if (isBreastfeeding) {
          const safePlantsBreastfeeding = ageCategories.find(c => c.id === 'breastfeeding')?.safePlants || [];
          const isSafeForBreastfeeding = safePlantsBreastfeeding.some(sp => plantName.toLowerCase().includes(sp.toLowerCase()));
          if (!isSafeForBreastfeeding || isPlantToAvoid) {
            notSuitableForChildren = true;
            childrenWarning = '⚠️ NON RECOMMANDÉ PENDANT L\'ALLAITEMENT. Les principes actifs passent dans le lait maternel.';
          }
        }
        // Vérification nourrisson
        else if (isInfant) {
          const safePlants = ageCategories.find(c => c.id === 'infant')?.safePlants || [];
          const isSafe = safePlants.some(sp => plantName.toLowerCase().includes(sp.toLowerCase()));
          if (!isSafe) {
            notSuitableForChildren = true;
            childrenWarning = 'Non recommandé pour les nourrissons (0-3 ans). Usage très restreint, avis médical requis.';
            minimumAge = 3;
          }
        } 
        // Vérification enfant
        else if (isChild) {
          if (isPlantToAvoid || (ageRestriction.hasRestriction && (patientAge ?? 0) < ageRestriction.minAge)) {
            notSuitableForChildren = true;
            minimumAge = ageRestriction.hasRestriction ? ageRestriction.minAge : 10;
            childrenWarning = `Non recommandé pour les enfants de moins de ${minimumAge} ans. ${ageCategory?.dosageNotes[0] || ''}`;
          }
        }
        
        // Ajouter les avertissements spécifiques pour personnes âgées
        let elderlyWarning = '';
        if (isElderly && isPlantToAvoid) {
          elderlyWarning = 'Prudence chez les personnes âgées : réduire les doses, surveiller les interactions médicamenteuses.';
        }
        
        // Construire la posologie adaptée à l'âge
        let adaptedPosology = plant.preparations || undefined;
        if (ageCategory && !notSuitableForChildren) {
          const forms = recommendedForms[ageCategory.id];
          if (forms && adaptedPosology) {
            adaptedPosology = `${adaptedPosology}\n\n📋 Adaptation ${ageCategory.label} : ${forms.notes}`;
          }
        }
        
        return {
          type: 'phytotherapie' as const,
          title: `${plantName} (${plant.latin_name})`,
          detail: `${plant.description} ${plant.main_effects?.slice(0, 3).join(', ')}.${elderlyWarning ? `\n\n⚠️ ${elderlyWarning}` : ''}`,
          posology: adaptedPosology,
          contraindications: plant.contraindications || [],
          priority: 2 as const,
          confidence: 0.75,
          notSuitableForChildren,
          minimumAge,
          childrenWarning: childrenWarning || undefined
        };
      });
    } catch (error) {
      console.error('Error in fetchMedicinalPlants:', error);
      return [];
    }
  }

  private async fetchBachFlowers(symptoms: string): Promise<Recommendation[]> {
    try {
      const { data: flowers, error } = await supabase
        .from('bach_flowers')
        .select('*');

      if (error) {
        console.error('Error fetching Bach flowers:', error);
        return [];
      }

      if (!flowers || flowers.length === 0) return [];

      const symptomsLower = symptoms.toLowerCase();
      
      // Filter flowers that match symptom keywords or effects
      const matchingFlowers = flowers.filter((flower: any) => 
        flower.symptoms_keywords?.some((keyword: string) => 
          symptomsLower.includes(keyword.toLowerCase())
        ) ||
        flower.main_effects?.some((effect: string) => 
          symptomsLower.includes(effect.toLowerCase())
        )
      );

      // Convert to Recommendation format (max 2 flowers to avoid overwhelming)
      return matchingFlowers.slice(0, 2).map((flower: any) => ({
        type: 'technique_bien_etre' as const,
        title: `Fleur de Bach : ${flower.name}`,
        detail: `${flower.description} Effets: ${flower.main_effects?.slice(0, 3).join(', ')}.`,
        posology: flower.usage_protocol,
        contraindications: flower.contraindications || [],
        priority: 2 as const,
        confidence: 0.7
      }));
    } catch (error) {
      console.error('Error in fetchBachFlowers:', error);
      return [];
    }
  }

  private convertTreatmentToRecommendation(resource: TreatmentResource, input: SymptomAnalysisInput): Recommendation {
    // Map vers les types autorisés dans la base de données:
    // 'nutrition', 'phytotherapie', 'hygiene_vie', 'complement', 'technique_bien_etre'
    const typeMap: Record<TreatmentResource['category'], Recommendation['type']> = {
      'aromatherapy': 'phytotherapie',      // Les HE sont de la phytothérapie
      'gemmotherapy': 'phytotherapie',      // La gemmothérapie est de la phytothérapie
      'clay': 'complement',                  // L'argile est un complément naturel
      'vegetable_oil': 'complement',         // Les huiles végétales sont des compléments
      'hydrolat': 'phytotherapie',          // Les hydrolats sont de la phytothérapie
      'apitherapy': 'complement'            // L'apithérapie est un complément naturel
    };
    
    const categoryLabels: Record<TreatmentResource['category'], string> = {
      'aromatherapy': 'Aromathérapie',
      'gemmotherapy': 'Gemmothérapie',
      'clay': 'Argile',
      'vegetable_oil': 'Huile Végétale',
      'hydrolat': 'Hydrolat',
      'apitherapy': 'Apithérapie'
    };

    const patientAge = input.age;
    const isPregnant = input.isPregnant === true;
    const isBreastfeeding = input.isBreastfeeding === true;
    const ageCategory = getAgeCategory(patientAge || 30, isPregnant, isBreastfeeding);
    
    // Détecter les restrictions d'âge dans les contre-indications
    const ageRestriction = this.extractAgeRestriction(resource.contraindications);
    const contraindicationsLower = resource.contraindications.map(c => c.toLowerCase()).join(' ');
    
    let notSuitableForChildren = false;
    let childrenWarning = '';
    let minimumAge: number | undefined;
    let adaptedPosology = resource.posology || resource.usage;
    
    // Vérifier grossesse
    if (isPregnant) {
      const isContraindicated = contraindicationsLower.includes('grossesse') || 
                               contraindicationsLower.includes('enceinte') ||
                               contraindicationsLower.includes('femmes enceintes');
      if (isContraindicated) {
        notSuitableForChildren = true;
        childrenWarning = '⚠️ CONTRE-INDIQUÉ PENDANT LA GROSSESSE. ' + 
          (resource.category === 'aromatherapy' ? 'Huiles essentielles à éviter pendant la grossesse.' : 'Usage non recommandé.');
      }
    }
    // Vérifier allaitement
    else if (isBreastfeeding) {
      const isContraindicated = contraindicationsLower.includes('allaitement') || 
                               contraindicationsLower.includes('allaitante');
      if (isContraindicated) {
        notSuitableForChildren = true;
        childrenWarning = '⚠️ CONTRE-INDIQUÉ PENDANT L\'ALLAITEMENT. Les principes actifs passent dans le lait maternel.';
      }
    }
    // Vérifier nourrisson (0-3 ans)
    else if (patientAge !== undefined && patientAge < 3) {
      // Huiles essentielles interdites pour nourrissons
      if (resource.category === 'aromatherapy') {
        notSuitableForChildren = true;
        childrenWarning = '⚠️ HUILES ESSENTIELLES INTERDITES pour les nourrissons (0-3 ans). Usage très restreint, avis médical requis.';
        minimumAge = 6;
      } else if (resource.category !== 'hydrolat') {
        notSuitableForChildren = true;
        childrenWarning = 'Non recommandé pour les nourrissons (0-3 ans). Seuls les hydrolats doux peuvent être envisagés.';
        minimumAge = 3;
      }
    }
    // Vérifier jeune enfant (3-10 ans)
    else if (patientAge !== undefined && patientAge < 10) {
      // Huiles essentielles très limitées
      if (resource.category === 'aromatherapy') {
        const isSafe = !ageRestriction.hasRestriction || (patientAge ?? 0) >= ageRestriction.minAge;
        if (!isSafe || ageRestriction.minAge > (patientAge ?? 0)) {
          notSuitableForChildren = true;
          childrenWarning = `⚠️ Non recommandé pour les enfants de moins de ${ageRestriction.minAge || 6} ans. Huiles essentielles à éviter en interne.`;
          minimumAge = ageRestriction.minAge || 6;
        } else {
          childrenWarning = '⚠️ Usage externe uniquement, dilution importante requise. Éviter HE en interne.';
        }
      }
    }
    // Vérifier préado/ado (10-16 ans)
    else if (patientAge !== undefined && patientAge < 16) {
      if (ageRestriction.hasRestriction && (patientAge ?? 0) < ageRestriction.minAge) {
        notSuitableForChildren = true;
        childrenWarning = `Non recommandé pour les moins de ${ageRestriction.minAge} ans. Adapter les doses au poids corporel.`;
        minimumAge = ageRestriction.minAge;
      }
    }
    // Vérifier personne âgée (65+ ans)
    else if (patientAge !== undefined && patientAge >= 65) {
      // Avertissements spécifiques pour personnes âgées
      const hasInteractionRisk = contraindicationsLower.includes('hypertension') ||
                                 contraindicationsLower.includes('anticoagulant') ||
                                 contraindicationsLower.includes('épilepsie');
      if (hasInteractionRisk) {
        childrenWarning = '⚠️ Prudence chez les personnes âgées : vérifier les interactions médicamenteuses. Réduire les doses de 25-50%.';
      }
    }
    
    // Adapter la posologie selon l'âge
    if (ageCategory && adaptedPosology && !notSuitableForChildren) {
      const forms = recommendedForms[ageCategory.id];
      if (forms) {
        adaptedPosology = `${adaptedPosology}\n\n📋 Adaptation ${ageCategory.label} : ${forms.notes}`;
      }
    }

    return {
      type: typeMap[resource.category],
      category: categoryLabels[resource.category],
      title: `${categoryLabels[resource.category]} : ${resource.name}${resource.latinName ? ` (${resource.latinName})` : ''}`,
      detail: `${resource.effects || ''} Indications: ${resource.indications.slice(0, 3).join(', ')}.`,
      posology: adaptedPosology,
      contraindications: resource.contraindications,
      priority: 2,
      confidence: 0.75,
      notSuitableForChildren,
      minimumAge,
      childrenWarning: childrenWarning || undefined
    };
  }

  private extractAgeRestriction(contraindications: string[]): { hasRestriction: boolean; minAge: number } {
    const agePatterns = [
      /enfants?\s*<?\.?\s*(\d+)\s*ans?/i,
      /moins\s*de\s*(\d+)\s*ans?/i,
      /<\s*(\d+)\s*ans?/i,
      /enfants?/i // Juste "enfants" sans âge = < 12 ans par défaut
    ];

    for (const ci of contraindications) {
      const ciLower = ci.toLowerCase();
      
      // Chercher un âge spécifique
      for (const pattern of agePatterns.slice(0, 3)) {
        const match = ciLower.match(pattern);
        if (match && match[1]) {
          return { hasRestriction: true, minAge: parseInt(match[1], 10) };
        }
      }
      
      // Si juste "enfants" mentionné sans âge spécifique
      if (ciLower.includes('enfant') && !ciLower.match(/\d+\s*ans/)) {
        return { hasRestriction: true, minAge: 12 }; // Par défaut < 12 ans
      }
    }

    return { hasRestriction: false, minAge: 0 };
  }

  private fetchTreatmentLibraryRecommendations(symptoms: string, input: SymptomAnalysisInput): Recommendation[] {
    const recommendations: Recommendation[] = [];
    
    // Aromathérapie
    const aromatherapyResults = findResourcesByCategoryAndSymptoms(symptoms, 'aromatherapy', 2);
    recommendations.push(...aromatherapyResults.map(r => this.convertTreatmentToRecommendation(r, input)));
    
    // Gemmothérapie
    const gemmotherapyResults = findResourcesByCategoryAndSymptoms(symptoms, 'gemmotherapy', 2);
    recommendations.push(...gemmotherapyResults.map(r => this.convertTreatmentToRecommendation(r, input)));
    
    // Argile
    const clayResults = findResourcesByCategoryAndSymptoms(symptoms, 'clay', 1);
    recommendations.push(...clayResults.map(r => this.convertTreatmentToRecommendation(r, input)));
    
    // Huiles végétales et Macérats
    const oilResults = findResourcesByCategoryAndSymptoms(symptoms, 'vegetable_oil', 2);
    recommendations.push(...oilResults.map(r => this.convertTreatmentToRecommendation(r, input)));
    
    // Hydrolats
    const hydrolatResults = findResourcesByCategoryAndSymptoms(symptoms, 'hydrolat', 2);
    recommendations.push(...hydrolatResults.map(r => this.convertTreatmentToRecommendation(r, input)));
    
    // Apithérapie
    const apitherapyResults = findResourcesByCategoryAndSymptoms(symptoms, 'apitherapy', 2);
    recommendations.push(...apitherapyResults.map(r => this.convertTreatmentToRecommendation(r, input)));
    
    return recommendations;
  }

  private async generateRecommendations(symptoms: string, input: SymptomAnalysisInput, userId?: string): Promise<Recommendation[]> {
    const patientAge = input.age;
    const isChild = patientAge !== undefined && patientAge <= 16;
    
    // First, get practitioner's custom treatments
    const practitionerTreatments = await this.fetchPractitionerTreatments(symptoms, userId);
    
    // Then, get relevant medicinal plants from the library
    const plantRecommendations = await this.fetchMedicinalPlants(symptoms, input);
    
    // Get relevant Bach flowers from the library
    const bachFlowerRecommendations = await this.fetchBachFlowers(symptoms);
    
    // Get recommendations from treatment library (aromatherapy, gemmotherapy, clay, vegetable oils)
    const treatmentLibraryRecommendations = this.fetchTreatmentLibraryRecommendations(symptoms, input);
    
    const recommendations: Recommendation[] = [
      ...practitionerTreatments, 
      ...plantRecommendations, 
      ...bachFlowerRecommendations,
      ...treatmentLibraryRecommendations
    ];

    // Recommandations digestives
    if (symptoms.includes('ballonnement') || symptoms.includes('digestif') || symptoms.includes('intestin')) {
      recommendations.push({
        type: 'nutrition',
        title: 'Amélioration de la digestion',
        detail: 'Adopter une alimentation anti-inflammatoire riche en fibres solubles et prébiotiques. Réduire progressivement les FODMAP si nécessaire.',
        posology: 'Intégrer quotidiennement légumes lacto-fermentés, graines de lin moulues (1 c.à.s/jour)',
        contraindications: ['Occlusion intestinale', 'Syndrome inflammatoire aigu'],
        priority: 1,
        confidence: 0.8
      });

      recommendations.push({
        type: 'phytotherapie',
        title: 'Plantes digestives',
        detail: 'Fenouil et menthe poivrée pour réduire les spasmes et améliorer le transit.',
        posology: 'Infusion : 1 c.à.c de mélange par tasse, 2-3 fois/jour après les repas',
        contraindications: ['Allergie aux ombellifères', 'Reflux gastro-œsophagien sévère'],
        priority: 2,
        confidence: 0.7
      });
    }

    // Recommandations fatigue
    if (symptoms.includes('fatigue') || symptoms.includes('énergie') || symptoms.includes('épuisement')) {
      recommendations.push({
        type: 'complement',
        title: 'Soutien énergétique naturel',
        detail: 'Complexe de vitamines B et magnésium pour soutenir le métabolisme énergétique.',
        posology: 'Magnésium bisglycinate 200mg le soir, Complexe B le matin',
        contraindications: ['Insuffisance rénale', 'Prise d\'antibiotiques (espacer de 2h)'],
        priority: 1,
        confidence: 0.75
      });

      recommendations.push({
        type: 'hygiene_vie',
        title: 'Optimisation du sommeil',
        detail: 'Établir une routine de sommeil régulière et améliorer l\'hygiène de vie générale.',
        posology: 'Coucher avant 22h30, réveil à heure fixe, exposition lumière naturelle le matin',
        contraindications: [],
        priority: 2,
        confidence: 0.9
      });
    }

    // Recommandations stress/anxiété
    if (symptoms.includes('stress') || symptoms.includes('anxiété') || symptoms.includes('nervosité')) {
      recommendations.push({
        type: 'phytotherapie',
        title: 'Gestion du stress',
        detail: 'Passiflore et mélisse pour apaiser le système nerveux sans créer de dépendance.',
        posology: 'Extrait fluide : 20-30 gouttes 2-3 fois/jour, ou infusion 15min avant les repas',
        contraindications: ['Grossesse', 'Allaitement', 'Prise de sédatifs'],
        priority: 1,
        confidence: 0.8
      });

      recommendations.push({
        type: 'technique_bien_etre',
        title: 'Techniques de relaxation',
        detail: 'Pratique quotidienne de cohérence cardiaque et respiration profonde.',
        posology: '3 séances de 5 minutes par jour (rythme 5s inspiration, 5s expiration)',
        contraindications: [],
        priority: 2,
        confidence: 0.85
      });
    }

    // Recommandations spécifiques femmes
    if (input.gender === 'femme' && (symptoms.includes('hormonal') || symptoms.includes('cycle') || symptoms.includes('règles'))) {
      recommendations.push({
        type: 'phytotherapie',
        title: 'Équilibre hormonal féminin',
        detail: 'Gattilier pour réguler naturellement les fluctuations hormonales.',
        posology: 'Extrait sec : 200mg le matin à jeun, en cure de 3 mois minimum',
        contraindications: ['Grossesse', 'Allaitement', 'Cancer hormono-dépendant', 'Traitement hormonal'],
        priority: 1,
        confidence: 0.7
      });
    }

    return recommendations;
  }

  private checkInteractions(medications: string[], allergies: string[], recommendations: Recommendation[]): InteractionAlert[] {
    const interactions: InteractionAlert[] = [];
    
    // Vérifier les interactions médicamenteuses pour chaque recommandation
    for (const rec of recommendations) {
      const recTitle = rec.title.toLowerCase();
      const recDetail = rec.detail.toLowerCase();
      
      for (const medication of medications) {
        // Utiliser la nouvelle fonction de vérification
        for (const interaction of plantMedicationInteractions) {
          const plantLower = interaction.plant.toLowerCase();
          
          if (recTitle.includes(plantLower) || recDetail.includes(plantLower)) {
            const medType = identifyMedicationType(medication);
            
            if (medType && interaction.medicationTypes.includes(medType.id)) {
              // Éviter les doublons
              const exists = interactions.some(i => 
                i.medication === medication && i.plant === interaction.plant
              );
              
              if (!exists) {
                interactions.push({
                  medication,
                  plant: interaction.plant,
                  severity: interaction.severity,
                  description: interaction.description + (interaction.mechanism ? ` (${interaction.mechanism})` : '')
                });
              }
            }
          }
        }
      }
    }
    
    // Vérifier les contre-indications liées aux allergies
    for (const rec of recommendations) {
      const allergyCheck = isPlantContraindicatedForAllergies(rec.title, allergies);
      
      if (allergyCheck.contraindicated && allergyCheck.reason) {
        const exists = interactions.some(i => 
          i.plant === rec.title && i.severity === 'major'
        );
        
        if (!exists) {
          interactions.push({
            medication: 'Allergie connue',
            plant: rec.title,
            severity: 'major',
            description: allergyCheck.reason
          });
        }
      }
    }
    
    return interactions;
  }

  private generateSources(symptoms: string): Source[] {
    const sources: Source[] = [
      {
        title: "ANSM - Agence nationale de sécurité du médicament",
        url: "https://ansm.sante.fr",
        summary: "Base de données officielle sur la sécurité des plantes médicinales et compléments alimentaires.",
        reliability: 'high'
      }
    ];

    if (symptoms.includes('digestif') || symptoms.includes('ballonnement')) {
      sources.push({
        title: "World Gastroenterology Organisation - Probiotics and Prebiotics",
        url: "https://www.worldgastroenterology.org",
        summary: "Recommandations internationales sur l'usage des probiotiques dans les troubles digestifs fonctionnels.",
        reliability: 'high'
      });
    }

    if (symptoms.includes('stress') || symptoms.includes('anxiété')) {
      sources.push({
        title: "NCCIH - National Center for Complementary and Integrative Health",
        url: "https://www.nccih.nih.gov",
        summary: "Études cliniques sur l'efficacité des plantes anxiolytiques comme la passiflore et la mélisse.",
        reliability: 'high'
      });
    }

    return sources;
  }

  private generateExplanations(symptoms: string, input: SymptomAnalysisInput): string {
    let explanation = "L'approche naturopathique proposée vise à identifier et traiter les causes sous-jacentes des symptômes décrits. ";
    
    if (symptoms.includes('digestif') || symptoms.includes('ballonnement')) {
      explanation += "Les troubles digestifs sont souvent liés à un déséquilibre du microbiote intestinal et à des habitudes alimentaires inadaptées. ";
    }
    
    if (symptoms.includes('fatigue')) {
      explanation += "La fatigue chronique peut résulter de carences nutritionnelles, d'un sommeil de mauvaise qualité ou d'un stress chronique. ";
    }
    
    if (symptoms.includes('stress')) {
      explanation += "Le stress chronique impacte de nombreux systèmes physiologiques et nécessite une approche globale combinant phytothérapie et techniques de gestion émotionnelle. ";
    }
    
    // Ajouter les informations sur les médicaments et allergies
    if (input.medications && input.medications.length > 0) {
      const summary = generateMedicationAllergySummary(input.medications, input.allergies || []);
      
      if (summary.medicationTypes.length > 0) {
        explanation += `\n\n💊 MÉDICAMENTS EN COURS :\n`;
        explanation += `Types identifiés : ${summary.medicationTypes.join(', ')}.`;
        
        if (summary.generalWarnings.length > 0) {
          explanation += "\n\n" + summary.generalWarnings.join("\n");
        }
      }
    }
    
    // Ajouter les informations sur les allergies
    if (input.allergies && input.allergies.length > 0) {
      const allergyWarnings = getAllergyWarningsForPatient(input.allergies);
      
      if (allergyWarnings.length > 0) {
        explanation += `\n\n🚨 ALLERGIES CONNUES :\n`;
        for (const warning of allergyWarnings) {
          explanation += `• ${warning.allergen.toUpperCase()} : ${warning.description}\n`;
          explanation += `  Plantes à éviter : ${warning.plantsToAvoid.slice(0, 5).join(', ')}${warning.plantsToAvoid.length > 5 ? '...' : ''}\n`;
        }
      }
    }
    
    // Ajouter les explications spécifiques selon l'âge
    if (input.age !== undefined) {
      const ageCategory = getAgeCategory(input.age, input.isPregnant, input.isBreastfeeding);
      if (ageCategory) {
        explanation += `\n\n⚠️ PRÉCAUTIONS LIÉES À L'ÂGE (${ageCategory.label} - ${ageCategory.ageRange}) :\n`;
        explanation += ageCategory.description + " ";
        
        if (ageCategory.warnings.length > 0) {
          explanation += "\n• " + ageCategory.warnings.join("\n• ");
        }
        
        const forms = recommendedForms[ageCategory.id];
        if (forms) {
          explanation += `\n\nFormes recommandées : ${forms.forms.join(', ')}. ${forms.notes}`;
        }
      } else if (input.age > 40 && input.age < 65) {
        explanation += "L'âge étant un facteur à considérer, les recommandations sont adaptées aux besoins spécifiques de cette tranche d'âge. ";
      }
    }
    
    if (input.gender === 'femme') {
      explanation += "\n\nLes spécificités physiologiques féminines, notamment les fluctuations hormonales, sont prises en compte dans cette approche. ";
    }
    
    explanation += "\n\nToutes les recommandations sont basées sur des données scientifiques actuelles et l'expérience clinique naturopathique, avec un niveau de prudence approprié.";
    
    return explanation;
  }

  private calculateConfidenceScore(symptoms: string, input: SymptomAnalysisInput): number {
    let score = 0.6; // Score de base
    
    // Plus d'informations = meilleur score
    if (input.age) score += 0.1;
    if (input.gender) score += 0.1;
    if (input.medications && input.medications.length > 0) score += 0.05;
    if (input.allergies && input.allergies.length > 0) score += 0.05;
    
    // Symptômes bien définis
    if (symptoms.length > 20) score += 0.1;
    
    return Math.min(score, 0.95); // Maximum 95%
  }
}

export const naturopathyService = new NaturopathyService();